import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RepoPrRow } from '../lib/repo-detail';
import { UNSET } from '../lib/unset';
import { ActionDrawer } from './ActionDrawer';
import { PrReviewRuns } from './PrReviewRuns';
import { StatusPill, type StatusTone } from './StatusPill';

// The pending queue's drafted-action drawer (#195, freshened by #1036).
// Presenter: every value it shows comes from `pr`, which the container fills
// from `GET /v1/prs/:id`. The list row only stands in while that read is in
// flight or after it failed — and `staleness` then says so, because a panel
// showing what the list last saw may never pass for server truth.

/** Why the panel may be showing the opening row: 404, or the read failed. */
export type PrStaleness = 'gone' | 'stale' | null;

export type PrPendingDrawerProps = {
  readonly pr: RepoPrRow;
  /** Human ref for the header — `owner/repo#<n>`, built by the container. */
  readonly title: string;
  readonly token: string | null;
  readonly staleness: PrStaleness;
  readonly onClose: () => void;
};

const ciTone = (ci: string): StatusTone =>
  ci === 'success' ? 'success' : ci === 'failure' || ci === 'error' ? 'danger' : 'neutral';

const detailLines = (pr: RepoPrRow): string =>
  [
    `${t('repo_detail.drawer_reviewers')}: ${pr.reviewers_seen.join(', ') || UNSET}`,
    `${t('repo_detail.drawer_signals')}: ${pr.ready_signals.join(', ') || UNSET}`,
  ].join('\n');

export function PrPendingDrawer(props: PrPendingDrawerProps): JSX.Element {
  return (
    <ActionDrawer open title={props.title} onClose={props.onClose}>
      <StatusPill tone={ciTone(props.pr.ci_status)}>
        {`${t('repo_detail.drawer_ci')}: ${props.pr.ci_status}`}
      </StatusPill>
      <pre class="code-block">{detailLines(props.pr)}</pre>
      <Show when={props.staleness}>
        {(kind) => (
          <p class="field__error" role="alert">
            {t(kind() === 'gone' ? 'repo_detail.pr_detail_gone' : 'repo_detail.pr_detail_error')}
          </p>
        )}
      </Show>
      <PrReviewRuns prId={props.pr.id} token={props.token} />
    </ActionDrawer>
  );
}
